import React, { useCallback, useState, useEffect, MouseEvent, ChangeEvent, ReactNode } from 'react';
import type { JSX } from 'react';
import styled from 'styled-components';

import { H3, Input, Spinner } from '@comeon/ui';

import CheckBox from '~/components/forms/checkbox';
import Portal from '~/components/portal';
import useLockCurrentContextScroll from '~/hooks/use-lock-current-context-scroll';
import getDevice from '~/libs/deviceDetector';
import { removeTransformNone, setTransformNone } from '~/utils/layout-utils';

// the wrapping 'div' has listeners to catch swipe-events from propagation to
// parent elements to block any underlying content to interact when the
// overlay is present. For example the 'react-slick' component adds listeners
// to the body-element. When these listeners are added to a static element
// some linterrors are triggered, and therefore disabled:
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
const onMouseMove = (e: React.MouseEvent): void => {
	e.stopPropagation();
};
const onTouchEnd = (e: React.TouchEvent): void => {
	e.stopPropagation();
};
const onTouchMove = (e: React.TouchEvent): void => {
	e.stopPropagation();
};
const onTouchStart = (e: React.TouchEvent): void => {
	e.stopPropagation();
};
const onMouseDown = (e: React.MouseEvent): void => {
	e.stopPropagation();
};
const onMouseUp = (e: React.MouseEvent): void => {
	e.stopPropagation();
};

type DeviceType = 'Mobile' | 'Tablet' | 'Desktop';
const DEVICE_TYPE = getDevice() as DeviceType;
const isMobile = DEVICE_TYPE === 'Mobile';
const isTablet = DEVICE_TYPE === 'Tablet';

const MOBILE_SET_TRANSFORM = 'main-layout';
const TABLET_SET_TRANSFORM = 'header-bigger';

interface TransformElements {
	Mobile: string;
	Tablet: string;
	Desktop?: string;
}

const TRANSFORM_ELEMENTS: TransformElements = {
	Mobile: MOBILE_SET_TRANSFORM,
	Tablet: TABLET_SET_TRANSFORM
};

const enableTransformNone = (): void => {
	setTransformNone(TRANSFORM_ELEMENTS[DEVICE_TYPE]);
};

const disableTransformNone = (): void => {
	removeTransformNone(TRANSFORM_ELEMENTS[DEVICE_TYPE]);
};

const CustomCheckbox = styled(CheckBox)`
	margin-top: 24px;
`;

const validateInput = (input: string | number | undefined, min?: number, max?: number): boolean => {
	/*
		based on the validation props(min, max, maxlength), "isInputInvalid" get filled
	*/
	return !(!input || (min !== undefined && min > Number(input)) || (max !== undefined && max < Number(input)));
};

interface ButtonWithLoadingProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	hasLoading?: boolean;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
	children: ReactNode;
}

export const ButtonWithLoading: React.FC<ButtonWithLoadingProps> = ({
	hasLoading = false,
	onClick,
	children,
	...other
}) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const internalOnClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			setIsLoading(true);
			onClick(event);
		},
		[setIsLoading, onClick]
	);

	return (
		<button onClick={internalOnClick} {...other}>
			{isLoading && hasLoading ? <Spinner /> : children}
		</button>
	);
};

interface WrapperProps {
	onClick: (event: MouseEvent<HTMLDivElement>) => void;
	children: ReactNode;
	forwadedRef?: React.RefObject<HTMLDivElement> | null;
	className?: string;
	lockContextScroll?: boolean;
}

export const Wrapper: React.FC<WrapperProps> = ({
	onClick,
	children,
	forwadedRef = null,
	className,
	lockContextScroll
}) => {
	useLockCurrentContextScroll(lockContextScroll);

	return (
		<div
			className={className}
			onMouseMove={onMouseMove}
			onTouchEnd={onTouchEnd}
			onTouchMove={onTouchMove}
			onTouchStart={onTouchStart}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			ref={forwadedRef}
			onClick={onClick}
		>
			{children}
		</div>
	);
};

type BinaryQuestionProps = {
	shouldOpenBinary?: boolean;
	title?: string;
	id?: string;
	text?: string | JSX.Element;
	yesText?: string;
	onNoClick?: (event: MouseEvent<HTMLButtonElement>) => void;
	noText?: string;
	hasInput?: boolean;
	inputValue?: string;
	inputType?: string;
	inputPattern?: string;
	onOutsideClick?: () => void;
	renderInParent?: boolean;
	overlayParent?: boolean;
	fixedPosition?: boolean;
	max?: number;
	min?: number;
	placeholder?: string;
	QRCodedata?: string | boolean;
	signUpUrl?: string | boolean;
	signUpText?: string;
	appUrlText?: string;
	transactionLoader?: ReactNode;
	validationMessage?: string;
	openInSidebar?: boolean;
	stickyPos?: boolean;
	className?: string;
	inputLabel?: string;
	children?: ReactNode;
	reverseOrder?: boolean;
	buttonsHasUncontrolledLoading?: boolean;
	Icon?: ReactNode;
	disableYesClick?: boolean;
	thirdText?: string;
	thirdKeyClick?: (event: MouseEvent<HTMLButtonElement>) => void;
	forwadedRef?: React.RefObject<HTMLDivElement>;
	enableInputValidation?: boolean;
	maxlength?: number;
	customValidate?: (value: string) => boolean;
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	disableSidebarScroll?: boolean;
};

type BinaryQuestionPropsDerived = {
	deriveInputStateFromProp: true;
	onYesClick?: (value: string) => void | Promise<void>;
} & BinaryQuestionProps;

type BinaryQuestionPropsNotDerived = {
	deriveInputStateFromProp?: false;
	onYesClick?: (event: MouseEvent<HTMLButtonElement>, inputValue?: string) => void | Promise<void>;
} & BinaryQuestionProps;

function BinaryQuestion(props: BinaryQuestionPropsDerived): JSX.Element | null;
function BinaryQuestion(props: BinaryQuestionPropsNotDerived): JSX.Element | null;
function BinaryQuestion({
	shouldOpenBinary,
	title,
	id,
	text,
	onYesClick: onYesClickCallback,
	yesText,
	onNoClick,
	noText,
	hasInput = false,
	inputValue: propInputValue,
	inputType,
	inputPattern,
	onOutsideClick,
	renderInParent,
	overlayParent,
	fixedPosition,
	max,
	min,
	placeholder,
	QRCodedata = false,
	signUpUrl = false,
	signUpText,
	appUrlText,
	transactionLoader = null,
	validationMessage,
	openInSidebar = false,
	stickyPos = false,
	className = '',
	inputLabel,
	children,
	reverseOrder,
	buttonsHasUncontrolledLoading = false,
	Icon,
	disableYesClick,
	thirdText,
	thirdKeyClick,
	forwadedRef,
	enableInputValidation,
	deriveInputStateFromProp,
	maxlength,
	customValidate,
	onChange: onChangeCallback,
	disableSidebarScroll
}: BinaryQuestionProps & (BinaryQuestionPropsDerived | BinaryQuestionPropsNotDerived)) {
	const [inputValue, setInputValue] = useState<string>(' ');
	const [touched, setTouched] = useState<boolean>(false);
	const [isValid, setIsValid] = useState<boolean>(!enableInputValidation || false);

	useEffect(() => {
		setIsValid(!enableInputValidation || validateInput(propInputValue, min, max));
	}, [enableInputValidation, propInputValue, min, max]);

	useEffect(() => {
		const isMobileOrTablet = isMobile || isTablet;

		// Only apply transform when on mobile/tablet and sidebar scroll is disabled
		if (disableSidebarScroll && isMobileOrTablet) {
			if (shouldOpenBinary) {
				enableTransformNone();
			} else {
				disableTransformNone();
			}
		}

		return () => {
			if (disableSidebarScroll && isMobileOrTablet) {
				disableTransformNone();
			}
		};
	}, [shouldOpenBinary, disableSidebarScroll]);

	useEffect(() => {
		if (!touched) {
			if (deriveInputStateFromProp && propInputValue === inputValue) {
				return;
			}
			if (propInputValue !== undefined) {
				setInputValue(propInputValue);
			}
			setTouched(true);
			setIsValid(!enableInputValidation || validateInput(propInputValue, min, max));
		}
	}, [propInputValue, deriveInputStateFromProp, min, max, enableInputValidation, touched, inputValue]);

	const onYesClick = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			if (!onYesClickCallback) {
				return;
			}

			if (deriveInputStateFromProp) {
				void onYesClickCallback(inputValue);
			} else {
				void onYesClickCallback(e, propInputValue);
			}
		},
		[deriveInputStateFromProp, onYesClickCallback, inputValue, propInputValue]
	);

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const { value } = e.currentTarget;
			const maxlengthExceeded = maxlength !== undefined && maxlength < value.length;

			if (!maxlengthExceeded) {
				/* I check maxLength here because If the value of the type attribute is text, email, search, password, tel, or url,
			this attribute specifies the maximum number of characters (in Unicode code points) that the user can enter;
			 for other control types, it is ignored. */
				const newIsValid =
					!enableInputValidation ||
					(validateInput(value, min, max) && (customValidate ? customValidate(value) : true));

				if (deriveInputStateFromProp) {
					e.persist();
					setInputValue(value);
					setIsValid(newIsValid);
				} else if (onChangeCallback) {
					onChangeCallback(e);
					setIsValid(newIsValid);
				}
			}
		},
		[maxlength, min, max, enableInputValidation, customValidate, deriveInputStateFromProp, onChangeCallback]
	);

	if (!shouldOpenBinary) {
		return null;
	}

	let binaryClasses = `binary-question u-flex u-fxb-c u-align-start notify notify--is-open ${className}`;
	if (overlayParent && !openInSidebar) {
		binaryClasses = `${binaryClasses} global-notification global-notification--is-open`;
	}
	if (fixedPosition) {
		binaryClasses = `${binaryClasses} binary-question--fixed`;
	}
	if (stickyPos && !isTablet) {
		binaryClasses = `${binaryClasses} binary-question--sticky u-oa`;
	}
	const lockContextScroll = disableSidebarScroll && shouldOpenBinary;

	const binaryQuestionContent = (
		<Wrapper
			className={binaryClasses}
			forwadedRef={forwadedRef}
			lockContextScroll={lockContextScroll}
			onClick={
				onOutsideClick
					? (event: MouseEvent<HTMLDivElement>) => {
							if ((event.target as HTMLElement).classList.contains('binary-question')) onOutsideClick();
						}
					: () => {}
			}
		>
			<div className={`binary-question__inner u-3-4-width u-b-radius ${stickyPos ? 'sticky' : ''}`}>
				<div className="binary-question__head u-p2 u-text-c">
					{Icon && Icon}
					{transactionLoader && <div>{transactionLoader}</div>}
					<H3 className="binary-question__main-title u-mb1">{title}</H3>
					<p className="js-text binary-question__p u-mb1">{text}</p>
					{hasInput &&
						(inputType === 'checkbox' ? (
							<CustomCheckbox
								id={id || ''}
								label={inputLabel || ''}
								iconPosition="left"
								onChange={onChange}
								validationMessage={isValid ? '' : validationMessage || ''}
								isChecked={deriveInputStateFromProp ? !!inputValue : !!propInputValue}
								dataAt="binary-checkbox"
							/>
						) : (
							<>
								{/* for password field in case of autoComplete. :')
										If this is not here then autoComplete will close the sidebar */}
								{inputType === 'password' && (
									<Input
										id="hidden-email-input"
										type="email"
										style={{
											position: 'absolute',
											width: '1px',
											height: '1px',
											margin: '-1px',
											border: '0',
											padding: '0',
											clip: 'rect(0 0 0 0)',
											overflow: 'hidden'
										}}
										onChange={() => {}}
									/>
								)}
								<Input
									autoFocus
									onChange={onChange}
									id={id || 'binary-input'}
									hasError={!isValid}
									value={deriveInputStateFromProp ? inputValue : propInputValue || ''}
									type={inputType}
									hideErrorIcon={inputType === 'password'}
									pattern={inputPattern}
									max={max}
									min={min}
									validationMessage={!isValid ? validationMessage || '' : ''}
									placeholder={placeholder}
									label={inputLabel}
									autoComplete="new-password"
								/>
							</>
						))}
					{children}
				</div>
				{QRCodedata && (
					<div className="binary-question__head u-p2 u-text-c qr-code">
						<img
							src={typeof QRCodedata === 'string' ? QRCodedata : ''}
							className=""
							alt="QRcode"
							style={{
								objectFit: 'cover'
							}}
						/>
					</div>
				)}
				{signUpUrl && (
					<div className="binary-question__head u-p2 u-text-c">
						<a
							href={typeof signUpUrl === 'string' ? signUpUrl : '/'}
							target="_blank"
							className="muchbetter-signup-link"
							rel="noopener noreferrer"
						>
							{QRCodedata ? appUrlText : signUpText}
						</a>
					</div>
				)}
				<div
					className={`binary-question__button-container u-b-radius-bottom ${
						thirdKeyClick ? 'u-center-vertically' : ''
					}`}
				>
					{thirdKeyClick && (
						<ButtonWithLoading
							type="button"
							data-at="bin-third"
							className="binary-question__third btn btn--secondary u-relative u-text-c"
							onClick={thirdKeyClick}
							hasLoading={buttonsHasUncontrolledLoading}
						>
							{thirdText}
						</ButtonWithLoading>
					)}
					{!reverseOrder && onNoClick && (
						<ButtonWithLoading
							type="button"
							data-at="bin-no"
							className={`${
								thirdKeyClick
									? 'binary-question__no btn btn--secondary u-relative u-text-c u-width-100'
									: 'binary-question__no btn btn--secondary u-relative u-text-c'
							}`}
							onClick={onNoClick}
							hasLoading={buttonsHasUncontrolledLoading}
						>
							{noText}
						</ButtonWithLoading>
					)}
					{onYesClickCallback && (
						<ButtonWithLoading
							type="button"
							data-at="bin-yes"
							className={`${
								thirdKeyClick
									? 'binary-question__yes btn btn--secondary u-relative u-text-c u-width-100'
									: 'binary-question__yes btn btn--secondary u-relative u-text-c'
							}`}
							onClick={onYesClick}
							disabled={!isValid || !!disableYesClick}
							hasLoading={buttonsHasUncontrolledLoading}
						>
							{yesText}
						</ButtonWithLoading>
					)}
					{reverseOrder && onNoClick && (
						<ButtonWithLoading
							type="button"
							data-at="bin-no"
							className={`${
								thirdKeyClick
									? 'binary-question__no btn btn--secondary u-relative u-text-c u-width-100'
									: 'binary-question__no btn btn--secondary u-relative u-text-c'
							}`}
							onClick={onNoClick}
							hasLoading={buttonsHasUncontrolledLoading}
						>
							{noText}
						</ButtonWithLoading>
					)}
				</div>
			</div>
		</Wrapper>
	);

	if (openInSidebar) {
		// FIXME_SIDEBAR
		return <Portal node={document && document.getElementById('sidebar')}>{binaryQuestionContent}</Portal>;
	}
	return renderInParent ? (
		<>{binaryQuestionContent}</> // eslint-disable-line react/jsx-no-useless-fragment
	) : (
		<Portal>{binaryQuestionContent}</Portal>
	);
}

export default BinaryQuestion;
